<!-- eslint-disable operator-linebreak -->
<!-- eslint-disable object-curly-newline -->
<script setup lang="ts">
  import { Modal } from 'bootstrap'
  import IconComponent from '../../../components/Atoms/Icon.vue'
  import InputSelect from '../components/Atoms/InputSelect.vue'
  import usePluginDownloadConfirmationModal from '../composables/usePluginDownloadConfirmationModal'
  import { UserOperateSystem, getOs } from '@/utils/getOs'
  import { useModalControl } from '../store/pinia/useModalControl.store'
  import sendClarityEvent from '@/utils/sendClarityEvent'
  import { useEmbedConfig } from '@/composables'

  import { ref, onMounted, computed } from 'vue'
  import { usePlugin } from '@/store/Pinia/usePlugin.store'

  const modal = ref<InstanceType<typeof Modal> | null>(null)
  const autoIdentifiedOS = ref<UserOperateSystem>(UserOperateSystem.UNKNOWN)
  const selectedOS = ref<UserOperateSystem>(UserOperateSystem.UNKNOWN)
  const selectedVersion = ref<any>(undefined)
  const modalControlStore = useModalControl()
  const pluginStore = usePlugin()
  const { isEmbedMode } = useEmbedConfig()

  const {
    showPluginDownloadConfirmationModal,
    hidePluginDownloadConfirmationModal,
  } = usePluginDownloadConfirmationModal()

  onMounted(() => {
    modal.value = new Modal('#modal-plugin-download', {
      backdrop: 'static',
      keyboard: false,
    })
  })

  const issuedAt = computed(() => pluginStore.issuedAt?.trim()?.length > 0)

  function hide() {
    modal.value?.hide()
    selectedOS.value = UserOperateSystem.UNKNOWN
  }

  function selectOs(OS: UserOperateSystem) {
    selectedOS.value = OS
    selectedVersion.value = undefined
  }

  const linuxIsSelected = computed(() => {
    if (selectedOS.value === UserOperateSystem.LINUX) return true
    return false
  })

  const linuxOptions = [
    { label: 'bird-sign-plugin.appimage', value: 1 },
    { label: 'bird-sign-plugin', value: 2 },
  ] as any

  const macIsSelected = computed(() => {
    if (selectedOS.value === UserOperateSystem.MACOS) return true
    return false
  })

  const macOptions = [{ label: 'birdid-sign-plugin.dmg', value: 1 }] as any

  const windowsIsSelected = computed(() => {
    if (selectedOS.value === UserOperateSystem.WINDOWS) return true
    return false
  })

  const windowsOptions = [
    { label: 'windows-32', value: 1 },
    { label: 'windows-64', value: 2 },
  ] as any

  const versionOptions = computed(() => {
    if (selectedOS.value === UserOperateSystem.LINUX) return linuxOptions
    if (selectedOS.value === UserOperateSystem.MACOS) return macOptions
    if (selectedOS.value === UserOperateSystem.WINDOWS) return windowsOptions
    return []
  })

  const versionLinks = [
    {
      label: 'bird-sign-plugin.appimage',
      link: 'https://s3-sa-east-1.amazonaws.com/repo.birdid.com.br/plugin/latest/Linux/Linux-64/BirdID-Sign-Plugin.AppImage',
    },
    {
      label: 'bird-sign-plugin',
      link: 'https://s3-sa-east-1.amazonaws.com/repo.birdid.com.br/plugin/latest/Linux/Linux-64/birdid-sign-plugin',
    },
    {
      label: 'birdid-sign-plugin.dmg',
      link: 'https://s3-sa-east-1.amazonaws.com/repo.birdid.com.br/plugin/latest/MacOS/BirdID-Sign-Plugin.dmg',
    },
    {
      label: 'windows-32',
      link: 'https://s3-sa-east-1.amazonaws.com/repo.birdid.com.br/plugin/latest/Windows/Windows-32/birdid-sign-plugin.exe',
    },
    {
      label: 'windows-64',
      link: 'https://s3-sa-east-1.amazonaws.com/repo.birdid.com.br/plugin/latest/Windows/Windows-64/birdid-sign-plugin.exe',
    },
  ]

  function downloadPluginVersion() {
    sendClarityEvent('downloadPlugin')
    const link = versionLinks.find(
      (version) => version.label === selectedVersion.value.label,
    )?.link
    if (link) window.open(link, '_blank')
    hide()
    modalControlStore.show({
      modalId: 'pluginDownloadConfirmationModal',
      show: showPluginDownloadConfirmationModal,
      hide: hidePluginDownloadConfirmationModal,
    })
  }

  function show() {
    const osInfo = getOs()
    autoIdentifiedOS.value = osInfo.os
    selectedOS.value = osInfo.os
    selectedVersion.value = osInfo.arch

    selectedVersion.value = 1

    if (osInfo.os === UserOperateSystem.LINUX) {
      selectedVersion.value = linuxOptions[1]
    }

    if (osInfo.os === UserOperateSystem.WINDOWS && osInfo.arch === 64) {
      selectedVersion.value = windowsOptions[1]
    }

    if (osInfo.os === UserOperateSystem.MACOS) {
      selectedVersion.value = macOptions[0]
    }

    if (osInfo.arch === undefined) {
      selectedVersion.value = undefined
    }

    if (osInfo.os === UserOperateSystem.ANDROID) {
      selectedVersion.value = undefined
    }

    if (osInfo.os === UserOperateSystem.IOS) {
      selectedVersion.value = undefined
    }

    modal.value?.show()
  }

  defineExpose({ show, hide })
</script>

<template>
  <div
    id="modal-plugin-download"
    class="modal modal-md fade"
    tabindex="-1"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header pb-3 border-bottom">
          <h5 class="modal-title plugin-modal-title">
            {{ !issuedAt ? 'Solicitação Aprovada' : 'Certificado emitido' }}!
          </h5>

          <button class="btn-close" @click="hide" />
        </div>
        <div class="modal-body py-3">
          <img
            v-if="
              !issuedAt && !(autoIdentifiedOS === UserOperateSystem.UNKNOWN)
            "
            class="desktop-img"
            alt="Desktop"
            src="../../../assets/images/desktop-pc.png"
          />

          <img
            v-if="issuedAt && !(autoIdentifiedOS === UserOperateSystem.UNKNOWN)"
            class="desktop-img-success"
            alt="Desktop"
            src="../../../assets/images/woman-desktop-success.svg"
          />

          <p v-if="!issuedAt" class="fs-6 text-start mt-4">
            Sua solicitação foi <strong>aprovada!</strong> Para prosseguir com a
            <strong>emissão do seu Certificado Digital</strong>
            é necessário baixar o plugin<span v-if="!isEmbedMode"
              >, "<strong>BirdID-Sign-Plugin</strong>"</span
            >
            em seu computador.
          </p>

          <div v-else class="fs-6 text-start mt-4">
            <p class="fs-6 text-start mt-4">
              <strong>Seu Certificado foi emitido com sucesso!</strong>
            </p>

            <p v-if="issuedAt" class="fs-6 text-start mt-4">
              Para baixá-lo, é necessário ter o
              <strong> plugin em execução </strong> no seu computador.
            </p>
          </div>

          <div v-if="autoIdentifiedOS === UserOperateSystem.UNKNOWN">
            <p class="fs-6 text-start mt-4">
              Escolha abaixo o Sistema Operacional abaixo:
            </p>

            <div class="d-flex justify-content-center gap-5 mt-5 mb-3">
              <div
                class="windows-container d-flex flex-column justify-content-center"
                @click="() => selectOs(UserOperateSystem.WINDOWS)"
                @keydown="() => selectOs(UserOperateSystem.WINDOWS)"
              >
                <img
                  v-if="windowsIsSelected"
                  class="blue-img"
                  alt="windows"
                  src="../assets/windows-blue.svg"
                />
                <img
                  v-else
                  class="black-img"
                  alt="windows"
                  src="../assets/windows-black.svg"
                />
                <span
                  class="mt-2"
                  :class="{ 'selected-label': windowsIsSelected }"
                >
                  Windows
                </span>
              </div>

              <div
                class="mac-container d-flex flex-column justify-content-center"
                @click="() => selectOs(UserOperateSystem.MACOS)"
                @keydown="() => selectOs(UserOperateSystem.MACOS)"
              >
                <img
                  v-if="macIsSelected"
                  class="blue-img"
                  alt="mac"
                  src="../assets/mac-blue.svg"
                />
                <img
                  v-else
                  class="black-img"
                  alt="windows"
                  src="../assets/mac-black.svg"
                />
                <span class="mt-2" :class="{ 'selected-label': macIsSelected }">
                  Mac
                </span>
              </div>

              <div
                class="linux-container d-flex flex-column justify-content-center"
                @click="() => selectOs(UserOperateSystem.LINUX)"
                @keydown="() => selectOs(UserOperateSystem.LINUX)"
              >
                <img
                  v-if="linuxIsSelected"
                  class="blue-img"
                  alt="linux"
                  src="../assets/linux-blue.svg"
                />
                <img
                  v-else
                  class="black-img"
                  alt="linux"
                  src="../assets/linux-black.svg"
                />
                <span
                  class="mt-2"
                  :class="{ 'selected-label': linuxIsSelected }"
                >
                  Linux
                </span>
              </div>
            </div>

            <div v-if="selectedOS != UserOperateSystem.UNKNOWN" class="mb-3">
              <p class="text-start fs-6">Selecione a versão</p>
              <input-select
                v-model="selectedVersion"
                :options="versionOptions"
                icon="download"
                placeholder="Selecione a versão do Sistema Operacional"
              />
            </div>
          </div>
        </div>
        <div class="modal-footer py-3">
          <button
            class="btn"
            :class="[
              selectedVersion === undefined ? 'btn-secondary' : 'btn-primary',
            ]"
            type="button"
            :disabled="selectedVersion === undefined"
            @click="downloadPluginVersion"
          >
            <icon-component class="me-2" name="download" />
            Baixar Plugin
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .plugin-modal-title {
    font-size: 1.5rem;
  }

  img {
    width: 60px;
    height: 60px;
  }

  .desktop-img {
    width: 10rem;
    height: 10rem;
  }

  .desktop-img-success {
    width: 12.25rem;
    height: 12.25rem;
  }

  .black-img {
    width: 60px;
    height: 60px;
  }

  .blue-img {
    width: 90px;
    height: 90px;
  }

  .windows-container,
  .mac-container,
  .linux-container {
    cursor: pointer;
  }

  .selected-label {
    color: #0080a3;
  }
</style>
